import React from 'react';
import { get, map, find, isEmpty } from 'lodash';
import ReactTooltip from 'react-tooltip';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { entityInfoCells as EntityInfoCells } from 'ui/views/widgets/entityInfoCells';
import { Icon } from 'modules/icon-bank';
import { MemberAddEditAddress } from 'ui/containers/MemberAddEditAddress';
import { MemberAddEditPaymentInformation } from 'ui/containers/MemberAddEditPaymentInformation';
import { checkbox as Checkbox } from 'ui/views/generic/checkbox';
import { button as Button } from 'modules/button';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { radioList as RadioList } from 'ui/views/generic/radioList';
import { inputField as InputField } from 'ui/views/generic/inputField';
import { ATTESTATION_PROVIDER } from 'constants/common';
import { DefaultPhoneNumberEnableAutoRefill } from 'ui/containers/DefaultPhoneNumberEnableAutoRefill';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { rxEnableAutoRefill as RxEnableAutoRefill } from 'ui/views/widgets/rxEnableAutoRefill';
 *
 * // render
 * <RxEnableAutoRefill propName='propValue'/>
 */
const _rxEnableAutoRefill = ( props ) => {
    const values = get( props, 'componentData.data.state', {} );
    const visibleItems = get( props, 'componentData.visibleItems', [] );

    const savedAddressesSection = find( visibleItems, { name: 'savedAddressesSection' }, {} );
    const isSavedAddressAvailable = ! isEmpty( savedAddressesSection );

    const paymentOptionsSection = find( visibleItems, {name: 'paymentOptionsSection' }, {} );
    const isPaymentOptionAvailable = ! isEmpty( paymentOptionsSection );

    const phoneNumberSection = find( visibleItems, {name: 'phoneNumberSection' }, {} );
    const isPhoneNumberAvailable = ! isEmpty( phoneNumberSection );

    // `title` is being sent as below to be compatible with existig MemberAddEditPaymentInformation container
    const paymentOptionWidgetConfig = {
        title: get( paymentOptionsSection, 'title', {} )
    };

    const checkboxInfo = find( visibleItems, { name: 'userConsent' }, {} );
    const isCheckboxInfoVisible = ! isEmpty( checkboxInfo );

    const attestationProviderSection = find( visibleItems, { name: 'attestationProvider' }, {} );
    const isNameSectionVisible = ATTESTATION_PROVIDER.CLINIC === props.attestationProvider || ATTESTATION_PROVIDER.CAREGIVER === props.attestationProvider;

    const patientAutorefillConsentSection = find( visibleItems, { name: 'patientAutorefillConsentSection' }, {} );
    const isCaliforniaUser = get( values, 'isCaliforniaUser', false );
    const isPatientAutorefillConsentVisible = isCaliforniaUser && ! isEmpty( patientAutorefillConsentSection );
    
    const attestationFields = get( patientAutorefillConsentSection, 'fields', [] );
    const consentStatusField = find( attestationFields, { name: 'consentStatus' }, {} );
    const patientStartDateField = find( attestationFields, { name: 'patientStartDate' }, {} );
    const patientEndDateField = find( attestationFields, { name: 'patientEndDate' }, {} );
    const attestationInfoCard = find( visibleItems, { name: 'attestationInfoCard' }, {} );
    const showDateFields = 'Accepted' === props.consentStatus;
    
    const patientAutorefillConsent = get( values, 'patientAutorefillConsent', {} );
    const existingConsentStatus = get( patientAutorefillConsent, 'consentStatus', '' );
    const existingStartDate = get( patientAutorefillConsent, 'patientStartDate', '' );
    const existingEndDate = get( patientAutorefillConsent, 'patientEndDate', '' );

    return (
        <div className='ui-rx-enable-auto-refill'>

            {/* main content */}
            <div className='ui-rx-enable-auto-refill__mainwrapper'>

                {/* title */}
                <div className='ui-rx-enable-auto-refill__mainwrapper__title'>
                    { getLabel( get( props, 'componentData.title' ) ) }
                </div>

                {/* subtitle */}
                <div className='ui-rx-enable-auto-refill__mainwrapper__subtitle'>
                    { getLabel( get( props, 'componentData.subtitle' ) ) }
                </div>

                {/* primary columns */}
                <div className='ui-rx-enable-auto-refill__mainwrapper__primary'>
                    <EntityInfoCells
                        items= { get( props, 'componentData.primaryColumns' ) }
                        values={ values }
                        className='ui-rx-enable-auto-refill__mainwrapper__primary__cell'
                    />
                </div>

                {/* widget info */}
                {
                    map( get( props, 'componentData.widgetInfoArray' ), ( infoObject, index ) => {
                        return (
                            <div className='ui-rx-enable-auto-refill__mainwrapper__widgetinfo' key={ index }>
                                <div className='ui-rx-enable-auto-refill__mainwrapper__widgetinfo__icon'>
                                    <Icon name='info' width='14' height='14' fill='#ffffff' />
                                </div>
                                <div className='ui-rx-enable-auto-refill__mainwrapper__widgetinfo__description'>
                                    { getLabel( get( infoObject, 'description', '' ) ) }
                                </div>
                            </div>
                        );
                    } )
                }

                {/* Default Phone Number section */}
                {
                    isPhoneNumberAvailable ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__phoneNumber'>
                            <DefaultPhoneNumberEnableAutoRefill
                                addAction={ find( visibleItems, { name: 'addPhoneNumberAction' }, {} ) }
                                phoneNumber={ get( values, 'phoneNumber', null ) }
                                infoCard={ find( visibleItems, { name: 'phoneNumberInfoCard' }, {} ) }
                                options={ get( props, 'componentData.data.options', {} ) }
                                title={ get( phoneNumberSection, 'title', {} ) }
                                handleFetchData = { props.handleFetchData }
                            />
                        </div>
                    ) : null
                }

                {/* saved addresses */}
                {
                    isSavedAddressAvailable ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__savedaddresses'>
                            <MemberAddEditAddress
                                addAction={ find( visibleItems, { name: 'addAddressAction' }, {} ) }
                                addresses={ get( values, 'savedAddresses', [] ) }
                                infoCard={ find( visibleItems, { name: 'addressInfoCard' }, {} ) }
                                options={ get( props, 'componentData.data.options', {} ) }
                                title={ get( savedAddressesSection, 'title', {} ) }
                                onAddEditAddressClick={ props.handleAddressAdd }
                            />
                        </div>
                    ) : null
                }

                {/* payment options */}
                {
                    isPaymentOptionAvailable ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__paymentoptions'>
                            <MemberAddEditPaymentInformation
                                addAction={ find( visibleItems, { name: 'addCardAction' }, {} ) }
                                addCardParams={ get( props, 'componentData.data.addCardParams', {} ) }
                                addPaymentRedirectPath={ props.addPaymentRedirectPath }
                                infoCard={ find( visibleItems, { name: 'paymentInfoCard' }, {} ) }
                                paymentOptions={ get( values, 'paymentOptions', [] ) }
                                widgetConfig={ paymentOptionWidgetConfig }
                            />
                        </div>
                    ) : null
                }

                 {
                    isPatientAutorefillConsentVisible ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation'>
                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__titleWrapper'>
                             <Icon id={ 'coverage' } height={ 20 } width={ 30 } />
                            
                            <div className='ui-member-add-edit-payment-information__header__title'>
                                { getLabel( get( patientAutorefillConsentSection, 'title', {} ) ) }
                            </div>
                            </div>

                   

                            {
                                attestationInfoCard ? (
                                    <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__info'>
                                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__info__icon'>
                                            <Icon name='info' width='14' height='14' fill='#ffffff' />
                                        </div>
                                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__info__description'>
                                            { getLabel( get( attestationInfoCard, 'description', '' ) ) }
                                        </div>
                                    </div>
                                ) : null
                            }

                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status'>
                                <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__label'>
                                    { getLabel( get( patientAutorefillConsentSection, 'currentStatusLabel', {} ) ) }
                                </div>
                                <div className={`ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__value 
                                    ${!existingConsentStatus ? 'ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__value--no-record' : ''}
                                    ${existingConsentStatus === 'Accepted' ? 'ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__value--accepted' : ''}
                                    ${existingConsentStatus === 'Refused' ? 'ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__value--refused' : ''}
                                    ${existingConsentStatus === 'No Response' ? 'ui-rx-enable-auto-refill__mainwrapper__california-attestation__current-status__value--no-response' : ''}`}>
                                    { existingConsentStatus || getLabel( get( patientAutorefillConsentSection, 'noRecordLabel', {} ) ) }
                                </div>
                            </div>

                            {
                                existingConsentStatus === 'Accepted' && existingStartDate && existingEndDate ? (
                                    <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates'>
                                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__start'>
                                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__start__label'>
                                                { getLabel( get( patientStartDateField, 'title', {} ) ) }
                                            </div>
                                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__start__value'>
                                                { existingStartDate }
                                            </div>
                                        </div>
                                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__end'>
                                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__end__label'>
                                                { getLabel( get( patientEndDateField, 'title', {} ) ) }
                                            </div>
                                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__existing-dates__end__value'>
                                                { existingEndDate }
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }

                            <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__edit-label'>
                                { getLabel( get( patientAutorefillConsentSection, 'editConsentLabel', {} ) ) }
                                <span className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__edit-label__asterisk'>
                                    *
                                </span>
                            </div>

                            {
                                consentStatusField ? (
                                    <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__consent-status'>
                                        <div className='ui-rx-enable-auto-refill__mainwrapper__california-attestation__consent-status__radiolist'>
                                            <RadioList
                                                options={ get( props, `componentData.data.options.${ patientAutorefillConsentSection.options }` ) }
                                                value={ props.consentStatus }
                                                onChange={ props.handleConsentStatusChange }
                                            />
                                        </div>
                                    </div>
                                ) : null
                            }
                        </div>
                    ) : null
                }

                {/* attestation provider */}
                {
                    attestationProviderSection ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__attestation-provider'>
                            <div className='ui-rx-enable-auto-refill__mainwrapper__attestation-provider__title'>
                                { getLabel( get( attestationProviderSection, 'title', {} ) ) }
                                <span className='ui-rx-enable-auto-refill__mainwrapper__attestation-provider__title__asterisk'>
                                    *
                                </span>
                            </div>
                            <div className='ui-rx-enable-auto-refill__mainwrapper__attestation-provider__radiolist'>
                                <RadioList
									options={ get( props, `componentData.data.options.${ attestationProviderSection.options }` ) }
									value={ props.attestationProvider }
									onChange={ props.handleAttestationProviderChange }
								/>
                            </div>
                            
                            {/* name section */}
                            {
                                isNameSectionVisible ? (
                                    <div className='ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section'>
                                        {
                                            map( get( attestationProviderSection, 'fields' ), ( field, index ) => {
                                                const fieldName = field.name;

                                                if ( ATTESTATION_PROVIDER.CLINIC === props.attestationProvider && 'lastName' === fieldName ) {
                                                    return null;
                                                }

                                                if ( ATTESTATION_PROVIDER.CAREGIVER === props.attestationProvider && 'lastInitials' === fieldName ) {
                                                    return null;
                                                }

                                                return (
                                                    <div className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }` } key={index}>
                                                        <div className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }__title` }>
                                                            <span>{ getLabel( get( field, 'title', {} ) ) }</span>
                                                            <span className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }__title__asterisk` }>
                                                                *
                                                            </span>

                                                            {/* info icon and tooltip */}
                                                            {
                                                                ( ATTESTATION_PROVIDER.CLINIC === props.attestationProvider && get( field, 'config.infoIcon', false ) ) ? (
                                                                    <span className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }__title__icon` }>
                                                                        <ReactTooltip
                                                                            id={ `${ fieldName }-tooltip` }
                                                                            aria-haspopup='true'
                                                                            textColor="#000"
                                                                            backgroundColor='#cdd7e1'
                                                                            className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }__title__icon__tooltip` }
                                                                            place="right"
                                                                        > { getLabel( get( field, 'config.infoText' ) ) } </ReactTooltip>

                                                                        <span data-tip  data-for={ `${ fieldName }-tooltip` } data-place="right">
                                                                            <Icon name='info' width='14' height='14' />
                                                                        </span>
                                                                    </span>
                                                                ) : null
                                                            }
                                                        </div>
                                                        <div className={ `ui-rx-enable-auto-refill__mainwrapper__attestation-provider__name-section__${ fieldName }__field` }>
                                                            <InputField
                                                                showLabel={ false }
                                                                placeholder={ field.placeholder }
                                                                name={ fieldName }
                                                                value={ 'firstName' === fieldName ? props[ 'firstName' ] : props[ 'lastName' ] }
                                                                onChange={ props.handleNameFieldChange }
                                                            />
                                                        </div>

                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                ) : null
                            }
                        </div>
                    ) : null
                }

                {/* checkbox info */}
                {
                    isCheckboxInfoVisible ? (
                        <div className='ui-rx-enable-auto-refill__mainwrapper__checkboxinfo'  onClick={ () => props.handleCheckboxClick() }>

                            {/* <div className='ui-rx-enable-auto-refill__mainwrapper__checkboxinfo__checkbox'> */}
                                <Checkbox
                                    size='xs'
                                    value={ props.userConsent }
                                    name= { checkboxInfo.name }
                                />
                            {/* </div> */}
                            <div className='ui-rx-enable-auto-refill__mainwrapper__checkboxinfo__text'>
                                { getLabel( get( checkboxInfo, 'description', {} ) ) }
                            </div>
                        </div>
                    ) : null
                }
            </div>

            {/* actions */}
            <div className='ui-rx-enable-auto-refill__actions'>
            {
                map( get( props, 'componentData.actions', [] ), action => {
                    return (
                        <Button
                            key={ action.name }
                            title={ action.title }
                            size='large'
                            outline={ 'cancel' === action.name ? true: false }
                            className='ui-rx-enable-auto-refill__actions__action'
                            disabled={ 'cancel' === action.name ? false : !props.enableSubmit }
                            onClick={ () => props.handleActionClick( { type: 'navAction', value: action } ) }
                        />
                    );
                } )
            }
            </div>
        </div>
    );
};

// set display name
_rxEnableAutoRefill.displayName = 'rxEnableAutoRefill';

// set default props
_rxEnableAutoRefill.defaultProps = {};

// prop types
_rxEnableAutoRefill.propTypes = {};


/**
 * @desc Preloader
 */
const rxEnableAutoRefillPreloader = () => {
    return (
        <div className='ui-rx-enable-auto-refill-preloader'>

            {/* main content */}
            <div className='ui-rx-enable-auto-refill-preloader__mainwrapper'>

                {/* title */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__title'></div>

                {/* subtitle */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__subtitle'></div>

                {/* primary columns */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__primary'>
                    {
                        map( [ 1, 2, 3, 4, 5 ], item => {
                            return (
                                <div key={ item } className='ui-rx-enable-auto-refill-preloader__mainwrapper__primary__cell'>
                                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__primary__cell__title'></div>
                                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__primary__cell__value'></div>
                                </div>
                            );
                        } )
                    }
                </div>

                {/* widget info */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__widgetinfo'>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__widgetinfo__icon'></div>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__widgetinfo__text'></div>
                </div>

                {/* saved addresses */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__savedaddresses'>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__savedaddresses__title'></div>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__savedaddresses__card'>
                        <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__savedaddresses__card__title'></div>
                        <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__savedaddresses__card__text'></div>
                    </div>
                </div>

                {/* payment options */}
                <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions'>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions__title'></div>
                    <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions__card'>
                        <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions__card__title'></div>
                        <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions__card__number'></div>
                        <div className='ui-rx-enable-auto-refill-preloader__mainwrapper__paymentoptions__card__expiry'></div>
                    </div>
                </div>
            </div>

            {/* actions */}
            <div className='ui-rx-enable-auto-refill-preloader__actions'>
                {
                    map( [ 1, 2 ], item => {
                        return (
                            <div key={ item } className='ui-rx-enable-auto-refill-preloader__actions__action'></div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
rxEnableAutoRefillPreloader.displayName = 'rxEnableAutoRefillPreloader';

// set default props
rxEnableAutoRefillPreloader.defaultProps = {};

// prop types
rxEnableAutoRefillPreloader.propTypes = {};


export const rxEnableAutoRefill = withNetworkState( rxEnableAutoRefillPreloader, _rxEnableAutoRefill );
